import {
  as400DivisionCode,
  matchRdStockAndItemMasterLocations,
  normalizeRdBranchLetter,
  rdDcForItemMasterLetter,
  rdDcForStockLetter,
  type RdLocationMatch,
} from '@erp/rpc';

export type RdSuspendedLocationClassification =
  | { kind: 'notRd' }
  | { kind: 'unknown' }
  | { kind: 'show'; match: Extract<RdLocationMatch, { status: 'mapped' }> }
  | {
      kind: 'hide';
      match: Extract<RdLocationMatch, { status: 'unmatched' }> | null;
    };

/**
 * ITEMSUSPE4 location cells are a single letter when they are a stock or
 * item-master code. Bins (`A-12`) and blanks are not location codes.
 */
export function rdLocationLetterFromCell(value: string): string {
  const letter = normalizeRdBranchLetter(value);
  return letter.length === 1 ? letter : '';
}

export function isRdSuspendedDivision(div: string): boolean {
  return as400DivisionCode(div) === 'RD';
}

function matchOneRdLetter(letter: string): RdLocationMatch | null {
  if (rdDcForStockLetter(letter)) {
    return matchRdStockAndItemMasterLocations({ stockLetter: letter });
  }
  if (rdDcForItemMasterLetter(letter)) {
    return matchRdStockAndItemMasterLocations({ itemMasterLetter: letter });
  }
  return matchRdStockAndItemMasterLocations({ stockLetter: letter });
}

/**
 * BRANCH and WRH LOCATION may each be stock or item-master. Try both
 * orientations; letter equality is never a Vera pair.
 */
function matchBothRdLetters(
  branch: string,
  warehouse: string,
): RdLocationMatch | null {
  const branchAsStock = matchRdStockAndItemMasterLocations({
    stockLetter: branch,
    itemMasterLetter: warehouse,
  });
  if (branchAsStock?.status === 'mapped') return branchAsStock;
  const branchAsItem = matchRdStockAndItemMasterLocations({
    stockLetter: warehouse,
    itemMasterLetter: branch,
  });
  if (branchAsItem?.status === 'mapped') return branchAsItem;
  return branchAsStock ?? branchAsItem;
}

/**
 * GEN-2668 / GEN-2659 / GEN-2660: RD Suspended Item rows are same-DC only
 * when the two location letters are a Vera pair (O↔C, D↔B, …). Other
 * divisions stay presence-only.
 *
 * `requireBothLetters` is the RD STOCK LOC / ITEM LOC layout (VL 2026-09-25):
 * both codes must be present; a blank side is not auto-filled (the `/C`
 * majority of that export is not a pair). BRANCH / WRH LOCATION keeps the
 * Option A single-letter fallback.
 */
export function classifyRdSuspendedLocations(args: {
  div: string;
  branch: string;
  warehouseLocation: string;
  requireBothLetters?: boolean;
}): RdSuspendedLocationClassification {
  if (args.div.trim() === '') return { kind: 'unknown' };
  if (!isRdSuspendedDivision(args.div)) return { kind: 'notRd' };
  const branch = rdLocationLetterFromCell(args.branch);
  const warehouse = rdLocationLetterFromCell(args.warehouseLocation);
  const match = args.requireBothLetters
    ? matchRequiredRdLetters(branch, warehouse)
    : matchOptionalRdLetters(branch, warehouse);
  if (match?.status === 'mapped') return { kind: 'show', match };
  return { kind: 'hide', match: match?.status === 'unmatched' ? match : null };
}

function matchRequiredRdLetters(
  branch: string,
  warehouse: string,
): RdLocationMatch | null {
  if (branch === '' || warehouse === '') return null;
  // STOCK LOC is always stock; ITEM LOC is always item-master. Do not
  // try the reversed pair (C/O is not a valid O/C).
  return matchRdStockAndItemMasterLocations({
    stockLetter: branch,
    itemMasterLetter: warehouse,
  });
}

function matchOptionalRdLetters(
  branch: string,
  warehouse: string,
): RdLocationMatch | null {
  if (warehouse === '') return matchOneRdLetter(branch);
  if (branch === '') return matchOneRdLetter(warehouse);
  return matchBothRdLetters(branch, warehouse);
}

export function rdSuspendedCorrespondenceFrom(
  location: RdSuspendedLocationClassification,
): {
  dc: string;
  stockLocation: string;
  itemMasterLocation: string;
} {
  if (location.kind !== 'show') {
    return { dc: '', stockLocation: '', itemMasterLocation: '' };
  }
  return {
    dc: location.match.dc,
    stockLocation: location.match.stockLetter,
    itemMasterLocation: location.match.itemMasterLetter,
  };
}
